import React, { useEffect, useState, useRef } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Nav } from "react-bootstrap";
import styled from "styled-components";

import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import useDevice from "../../customHooks/useDevice";
import { useAnalytics } from "../../helpers/AnalyticsTrackingContext";
import Loader from "../Loader/Loader";
import ModalCloseButton from "../Modal/ModalCloseButton";
import VacancyHead from "../VacancyHead/VacancyHead";
import VacancyBar from "./VacancyBar";

const StyledForm = styled.div`
  flex-grow: 1;
  height: 80vh;

  @media (min-width: ${breakpoints.DESKTOP}) {
    height: 90vh;
  }

  overflow: hidden;
`;

const StyledNav = styled(Nav)`
  background-color: ${({ theme, backgroundColor }) =>
    `${backgroundColor || theme.secondaryBackgroundColor}`};
  color: ${({ textColor }) => textColor};
`;

function ApplicationFormModal(props) {
  const { thumbnailUrl, formHeaderBackgroundColor, vacancy } = props;

  const { sendEvent } = useAnalytics();
  const { modalStore, configStore, vacancyStore, trackingStore } = useStores();

  const { config } = configStore;
  const { facebookPixelId, googleMeasurementID } = config;
  const { closeModal } = modalStore;
  const { selectedVacancy } = vacancyStore;
  const { refererQueryString, trackingId, sessionId } = trackingStore;

  const hideLogo = config?.applicationFormConfig?.hideLogo || false;
  const currentVacancy = vacancy || selectedVacancy;
  const formHeaderTextColor =
    config?.applicationFormConfig?.textColor || "#FFFFFF";

  const backgroundColor = config?.applicationFormConfig?.backgroundColor;

  const [isIframeLoading, setIsIframeLoading] = useState(true);

  const { isMobile } = useDevice();

  const iframeRef = useRef();

  const applicationFormUrl =
    currentVacancy.applicationDetail?.url || currentVacancy.applicationUrl;

  const handleIframeClose = (event) => {
    const { data } = event;
    if (data === "doClose") {
      if (iframeRef.current) {
        iframeRef.current.contentWindow.postMessage("beforeUnloadCalled", "*");
      }
      if (!isEmpty(facebookPixelId)) {
        window.fbq("track", "SubmitApplication");
      }
      if (!isEmpty(googleMeasurementID) && "gtag" in window) {
        window.gtag({ event: "sign_up" });
      }
      closeModal();
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleIframeClose);
    return () => window.removeEventListener("message", handleIframeClose);
  }, []);

  useEffect(() => {
    modalStore.onModalCloseEvent = () => {
      if (currentVacancy.id) {
        sendEvent({
          name: "EXIT_FORM",
          category: "FORM_EVENT",
          label: "EXIT_FORM",
          flush: true,
          data: {
            formId: currentVacancy.id,
          },
        });
      }
    };
  }, [currentVacancy.id]);

  let iframeSrc = [
    `${applicationFormUrl}&hideLogo=${hideLogo}`,
    refererQueryString,
  ]
    .filter(Boolean)
    .join("&");

  if (trackingId || sessionId) {
    iframeSrc += `${trackingId ? `&tid=${trackingId}` : ""}${
      sessionId ? `&sid=${sessionId}` : ""
    }`;
  }

  return (
    <>
      <StyledNav
        backgroundColor={backgroundColor || formHeaderBackgroundColor}
        className="d-flex justify-content-between p-4 flex-nowrap"
        textColor={formHeaderTextColor}
        title={currentVacancy.title || currentVacancy?.jobTitle}
      >
        {isMobile ? (
          <VacancyBar
            textColor={formHeaderTextColor}
            thumbnailUrl={thumbnailUrl}
          />
        ) : (
          <VacancyHead
            isForm
            thumbnailUrl={thumbnailUrl}
            vacancy={currentVacancy}
          />
        )}
        <ModalCloseButton
          backgroundColor={backgroundColor || formHeaderBackgroundColor}
          onClick={closeModal}
          textColor={formHeaderTextColor}
        />
      </StyledNav>
      <StyledForm className="d-flex flex-column position-relative">
        {isIframeLoading && (
          <div className="position-absolute start-0 end-0 bottom-0 top-0 d-flex justify-content-center align-items-center bg-white">
            <Loader />
          </div>
        )}
        {currentVacancy && (
          <iframe
            allow="camera; microphone"
            className="h-100 w-100 flex-grow-1"
            data-testid="application-form"
            onLoad={() => setIsIframeLoading(false)}
            src={iframeSrc}
            title="applyModal"
          />
        )}
      </StyledForm>
    </>
  );
}

ApplicationFormModal.defaultProps = {
  formHeaderBackgroundColor: "",
  thumbnailUrl: "",
};

ApplicationFormModal.propTypes = {
  formHeaderBackgroundColor: PropTypes.string,
  thumbnailUrl: PropTypes.string,
};

export default observer(ApplicationFormModal);
